import React, { Component, Fragment } from 'react';
import { compose } from '../../reactApolloCompat';
import { withRouter } from '../../util/withRouter';
import gql from 'graphql-tag';
import withStyles from '@mui/styles/withStyles';
import { withErrorBoundary } from '@sentry/react';
import Grid from '../../component/form/Grid';
import { Paper } from '@mui/material';
import DataTable from '../../component/dataTable/DataTable';
import LinkButton from '../../component/form/LinkButton';
import { joinDefined, PAGE_STOCK, prettyPrice } from '../../util/strings';
import ErrorFallback from '../../component/ErrorFallback';
import defaultImage from '../../_asset/default-image.png';
import Typography from '../../component/Typography';
import { getOffices } from '../../environment';
import GatedComponent from '../../component/GatedComponent';
import Button from '../workQueue2/extras/Button';
import CloseIcon from '../../component/icon/CloseIcon';

class StockList extends Component {
    render() {
        const { classes, history } = this.props;
        return (
            <div>
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={12}>
                        <Typography variant="display4" gutterBottom className={classes.pageTitle}>
                            Catalogue
                            <GatedComponent showComponent={'CMS_ACCESS_SilverShop\\Admin\\ProductCatalogAdmin'}>
                                {() => (
                                    <Button
                                        style={{ float: 'right' }}
                                        variant="primary icon-button white shadow"
                                        size="lg"
                                        title="Add a new catalogue item"
                                        onClick={() => history.push('0')}
                                    >
                                        <CloseIcon style={{ transform: 'rotate(45deg)' }} />
                                    </Button>
                                )}
                            </GatedComponent>
                        </Typography>
                    </Grid>
                </Grid>
                <Paper className={classes.paper} elevation={0}>
                    <div className={classes.wideScroll}>{this.renderTable()}</div>
                </Paper>
            </div>
        );
    }

    renderTable() {
        return (
            <Fragment>
                <Grid item>
                    <Grid container spacing={3}>
                        <Grid item xs={12} style={{ position: 'relative' }}>
                            <DataTable
                                dataObject="Product"
                                queryName="readProducts"
                                fragment={stockFragment}
                                isFilterable={['Brand', 'Category']}
                                isSortable
                                columns={tableColumns}
                                variables={{
                                    filters: [{ field: 'AllowPurchase', value: '1' }],
                                    filterBy: [],
                                    sortBy: [{ field: 'InternalItemID', direction: 'ASC' }]
                                }}
                                searchVariable="contains"
                                noFiltersTitle="All Products & Services"
                                // showFlags
                                persistVariablesKey="Stock"
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </Fragment>
        );
    }
}

export const tableColumns = [
    {
        label: 'Item Code/SKU',
        propertyPath: 'InternalItemID',
        renderCell: (row, value) => <LinkButton href={PAGE_STOCK + row.ID + '/' + row.InternalItemID} text={value} />
    },
    {
        label: 'Photo',
        tiny: true,
        className: 'td--not-padded align-center',
        propertyPath: 'ImageURL',
        renderCell: (row, value) => (
            <img
                src={value || defaultImage}
                width={48}
                height={48}
                alt="Product thumbnail"
                style={{ objectFit: 'contain', objectPosition: 'center', verticalAlign: 'middle' }}
            />
        )
    },
    {
        label: 'Title',
        propertyPath: 'Title'
    },
    {
        label: 'Variations',
        propertyPath: 'Variations',
        headerStyle: { minWidth: 100 },
        renderCell: (row, value) =>
            value.nodes.map((obj, idx) => (
                <div key={obj.ID}>
                    <small title={obj.AttributeTitle}>
                        {!!obj.ShortDescription || '' ? obj.ShortDescription + ': ' : ''}
                        <LinkButton href={PAGE_STOCK + row.ID + '/' + obj.InternalItemID} text={obj.InternalItemID} />
                    </small>
                </div>
            ))
    },
    {
        label: 'Description',
        propertyPath: 'ContentSummary',
        renderCell: (row, value) => value
    },
    {
        label: 'Categories',
        propertyPath: 'Parent',
        renderCell: (row, value) => {
            const vals = row.ProductCategories.map(obj => obj.Title) || [];
            return (
                <small>
                    <strong>{value.Title}</strong>
                    {!!vals.length && ', '}
                    {joinDefined(vals, ', ')}
                </small>
            );
        }
    },
    {
        label: 'Sale Price',
        propertyPath: 'SellingPrice',
        className: 'align-right',
        renderCell: (row, value) => prettyPrice(value)
    },
    {
        label: 'Brand Prices',
        headerStyle: { minWidth: 100 },
        propertyPath: 'PriceOverrides',
        className: 'align-right',
        renderCell: (row, value) =>
            value.nodes.map(obj => {
                const office = getOffices().find(off => obj.ID === off.ID);
                return (
                    <div title={office && office.Title} key={obj._join.PriceOverride.ID}>
                        <small>
                            {office && office.RegistrantSuffix}: {prettyPrice(obj._join.PriceOverride.SellingPrice)}
                        </small>
                    </div>
                );
            })
    },
    {
        label: 'Cost',
        propertyPath: 'PurchasePrice',
        className: 'align-right',
        renderCell: (row, value) => prettyPrice(value)
    }
];

export const stockFragment = gql`
    fragment StockRecord on Product {
        ID
        InternalItemID
        Title
        ContentSummary
        SellingPrice
        PurchasePrice
        ImageURL
        AllowPurchase
        Parent {
            ID
            Title
        }
        ProductCategories {
            ID
            Title
        }
        PriceOverrides {
            nodes {
                ID
                ... on OfficesUsername {
                    _join {
                        PriceOverride {
                            ID
                            SellingPrice
                        }
                    }
                }
                ... on OfficesBranch {
                    _join {
                        PriceOverride {
                            ID
                            SellingPrice
                        }
                    }
                }
            }
        }
        Variations {
            nodes {
                ID
                InternalItemID
                AttributeTitle
                ShortDescription
            }
        }
    }
`;
const styles = ({ spacing, typography, palette, breakpoints, funeralHome }) => ({
    paper: {
        padding: spacing(3)
    },
    pageTitle: {
        color: palette.contentForeground[funeralHome]
    },
    header: {
        fontWeight: typography.fontWeightLight,
        marginLeft: 16,
        paddingLeft: 16,
        borderLeft: '1px solid ' + palette.action.active,
        color: palette.text.primary
    },
    wideScroll: {
        overflow: 'visible',
        overflowY: 'auto',
        padding: 12,
        paddingTop: 0,
        paddingLeft: 24,
        marginLeft: -12,
        '& td > div + div': {
            marginTop: 4
        }
    },
    inline: {
        display: 'flex',
        alignItems: 'center',
        position: 'relative'
    }
});
export default withErrorBoundary(compose(withRouter, withStyles(styles))(StockList), { fallback: ErrorFallback });
